import React, { useEffect, useMemo } from "react"

import { useNavigate } from "react-router-dom"
import { ViewportList } from "react-viewport-list"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"

import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"
import { UtilHelper } from "@front-shared/helpers/util.helper"

import { CoverageCheckerRunningState } from "../../../config/checker.config"
import { CSV_CHECKER_CONFIG } from "../../../config/csvChecker.config"
import { GENERAL_CONFIG } from "../../../config/general.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { ScreenScrollableTableElement } from "../../../elements/screenScrollableTable.element"
import { CheckerHelper } from "../../../helpers/checker.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { userDetailsSelector } from "../../../store/selectors/userDetails.selector"
import {
    checkerRemoveContinuousMonitoringInputData,
    checkerRemoveMedicaidInputData,
    checkerReset,
    checkerSetRunningState,
} from "../../../store/slices/checker.slice"
import {
    checkerRunDiscoverChecks,
    checkerRunGeneralChecks,
    checkerRunMedicaidChecks,
    checkerRunSmartScanChecks,
} from "../../../store/thunks/checker.thunks"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types"
import type { IThunkActionWithPracticeData } from "../../../types/general.types"
import type {
    IContinuousMonitoringCoverageCheck,
    IMedicaidCoverageCheck,
} from "../../../types/objects.types"
import { BulkResultsInsightsComponent } from "../bulkResults/bulkResultsInsights.component"
import { CheckTypeSelectorComponent } from "../checkTypeSelector.component"
import { RunCsvChecksFloatingButtonComponent } from "./runCsvChecksFloatingButton.component"
import { SingleCsvCoverageCheckComponent } from "./singleCsvCoverageCheck.component"
import { UploadCsvComponent } from "./uploadCsv.component"

export const CoverageCheckerCSVComponent: React.FunctionComponent<
    ICoverageCheckerCSVComponentProps
> = props => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const checkerState = useAppSelector(checkerSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { selectedPracticeUsageQuota } = useAppSelector(userDetailsSelector)

    const earlyReleaseFeaturesEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_EARLY_RELEASE_FEATURES,
    )

    const coverageChecks = useMemo<
        IMedicaidCoverageCheck[] | IContinuousMonitoringCoverageCheck[]
    >(() => {
        switch (checkerState.csvCheckType) {
            case QuotaUsageType.GENERAL:
            case QuotaUsageType.DISCOVER:
            case QuotaUsageType.SMART_SCAN:
            case QuotaUsageType.AGENTIC_PORTAL:
            case QuotaUsageType.AGENTIC_PHONE:
                return checkerState.continuousMonitoringCoverageChecks

            case QuotaUsageType.MEDICAID:
                return checkerState.medicaidChecks
        }
    }, [
        checkerState.csvCheckType,
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.medicaidChecks,
    ])

    const resultsCount = useMemo<number>(() => {
        return (
            (coverageChecks as { result?: any | null }[]).filter(
                item => !!item?.result,
            ).length || 0
        )
    }, [coverageChecks])

    const isInputView = useMemo<boolean>(() => {
        return [
            CoverageCheckerRunningState.INPUT_CSV,
            CoverageCheckerRunningState.PROCESS_CSV,
        ].includes(checkerState.runningState)
    }, [checkerState.runningState])

    const isValidInputRow = (row: IDynamicCheckerFormInputData): boolean => {
        return !row.csvValidationErrors?.length
    }

    const getValidInputStateRows = () => {
        return coverageChecks
            .map(
                (
                    item:
                        | IMedicaidCoverageCheck
                        | IContinuousMonitoringCoverageCheck,
                ) => item.inputData,
            )
            .filter(isValidInputRow)
    }

    const validInputRowsCount = useMemo<number>(() => {
        return getValidInputStateRows().length
    }, [checkerState.inputDataSetAt])

    // If not coverage checks are present, we need to reset the state
    useEffect(() => {
        if (!coverageChecks.length) {
            dispatch(checkerReset())
        }
    }, [coverageChecks])

    const performCalculationWithQuotaConfirmation = () => {
        if (!selectedPracticeUsageQuota) {
            return
        }

        CheckerHelper.runChecksWithQuotaConfirmation(
            selectedPracticeUsageQuota,
            performCalculation,
            checkerState.csvCheckType,
            getValidInputStateRows().length,
        )
    }

    const performCalculation = () => {
        if (!selectedPracticeRole) {
            return
        }

        // Preview table can be scrolled far down when checks are triggered,
        // and results replace it in place
        UtilHelper.scrollToPageTop()

        const onSuccess = () => {
            dispatch(
                checkerSetRunningState(CoverageCheckerRunningState.RESULTS_CSV),
            )
        }

        let triggered = false

        const thunkPayload: IThunkActionWithPracticeData<
            IDynamicCheckerFormInputData[]
        > = {
            payload: getValidInputStateRows(),
            practice: selectedPracticeRole.practice,

            onSuccess,
        }

        switch (checkerState.csvCheckType) {
            case QuotaUsageType.GENERAL:
                dispatch(checkerRunGeneralChecks(thunkPayload))

                triggered = true

                break

            case QuotaUsageType.DISCOVER:
                dispatch(checkerRunDiscoverChecks(thunkPayload))

                triggered = true

                break

            case QuotaUsageType.SMART_SCAN:
                dispatch(checkerRunSmartScanChecks(thunkPayload))

                triggered = true

                break

            case QuotaUsageType.MEDICAID:
                dispatch(checkerRunMedicaidChecks(thunkPayload))

                triggered = true

                break

            case QuotaUsageType.AGENTIC_PORTAL:
            case QuotaUsageType.AGENTIC_PHONE:
                ToastrHelper.warning(
                    `${checkerState.csvCheckType} check type is not available yet.`,
                )

                break
        }

        // Add random part to the url, so we can safely catch browser back button then
        // And if back button is clicked from showing results -> it will initiate "run new checks"
        // Acts as history.push
        triggered &&
            navigate(
                `${ROUTES_CONFIG.coverageCheckerUrl}?${
                    GENERAL_CONFIG.urlSearchParamsKeys.results
                }=${new Date().getTime()}`,
            )
    }

    const onRemoveCoverageInputData = (
        coverageInputData: IDynamicCheckerFormInputData,
    ) => {
        switch (checkerState.csvCheckType) {
            case QuotaUsageType.GENERAL:
            case QuotaUsageType.DISCOVER:
            case QuotaUsageType.SMART_SCAN:
            case QuotaUsageType.AGENTIC_PORTAL:
            case QuotaUsageType.AGENTIC_PHONE:
                dispatch(
                    checkerRemoveContinuousMonitoringInputData(
                        coverageInputData,
                    ),
                )

                break

            case QuotaUsageType.MEDICAID:
                dispatch(checkerRemoveMedicaidInputData(coverageInputData))

                break
        }
    }

    const tableHeaders = useMemo<JSX.Element>(() => {
        return (
            <div className="table-row w-full">
                <div
                    className="
                        flex items-center px-[16px] py-[8px]
                        bg-brand-white
                    "
                >
                    <div className="mr-[16px] flex-shrink-0 w-[100px]">
                        <PrimaryText
                            className="opacity-75 flex items-center"
                            typography="h6"
                        >
                            Row #
                        </PrimaryText>
                    </div>

                    {CSV_CHECKER_CONFIG.uploadCsvHeaderKeys[
                        checkerState.csvCheckType
                    ]
                        .filter(
                            key =>
                                !CSV_CHECKER_CONFIG.earlyReleaseItems.fields.includes(
                                    key,
                                ) || earlyReleaseFeaturesEligible,
                        )
                        .map((key, index) => {
                            const columnConfig =
                                CSV_CHECKER_CONFIG.uploadCsvPreviewMapping[key]

                            return columnConfig ? (
                                <div
                                    key={index}
                                    className="mr-[16px] flex-shrink-0 w-[250px]"
                                >
                                    <PrimaryText
                                        className="opacity-75 flex items-center"
                                        typography="h6"
                                    >
                                        {columnConfig.header}
                                    </PrimaryText>
                                </div>
                            ) : null
                        })}
                </div>
            </div>
        )
    }, [checkerState.csvCheckType, earlyReleaseFeaturesEligible])

    return (
        <div
            className={`
                ${props.className}
                relative
            `}
        >
            <div className="mt-[24px] flex items-stretch">
                {isInputView ? (
                    <CheckTypeSelectorComponent className="w-[220px] rounded-l-lg" />
                ) : null}

                <div
                    className={`
                        relative ${!isInputView ? "flex-1" : ""}
                    `}
                >
                    {[
                        CoverageCheckerRunningState.INPUT_CSV,
                        CoverageCheckerRunningState.PROCESS_CSV,
                    ].includes(checkerState.runningState) ? (
                        <UploadCsvComponent className="w-[800px] rounded-r-lg overflow-hidden" />
                    ) : resultsCount ? (
                        <>
                            <BulkResultsInsightsComponent
                                className="-mt-[8px]"
                                checkType={checkerState.csvCheckType}
                            />

                            {coverageChecks.length > resultsCount ? (
                                <div
                                    className="
                                        mt-[44px] flex flex-col items-center justify-center px-[16px] py-[24px]
                                        rounded-[8px] bg-brand-warmLight025
                                    "
                                >
                                    <PrimaryText typography="h5" centered>
                                        Please note that it takes approximately
                                        30 minutes to run 1000 checks. Please
                                        keep your tab open until the checks are
                                        complete.
                                    </PrimaryText>
                                </div>
                            ) : null}
                        </>
                    ) : coverageChecks.length &&
                      checkerState.runningState ===
                          CoverageCheckerRunningState.PREVIEW_CSV ? (
                        <>
                            <ScreenScrollableTableElement
                                headers={tableHeaders}
                            >
                                <ViewportList<
                                    | IMedicaidCoverageCheck
                                    | IContinuousMonitoringCoverageCheck
                                >
                                    items={coverageChecks}
                                >
                                    {(item, index) => (
                                        <SingleCsvCoverageCheckComponent
                                            key={index}
                                            rowIndex={index}
                                            coverageCheck={item}
                                            onInputDataRemove={
                                                onRemoveCoverageInputData
                                            }
                                        />
                                    )}
                                </ViewportList>
                            </ScreenScrollableTableElement>

                            {/*RUN COVERAGE CHECKS BTN*/}
                            {checkerState.runningState ===
                            CoverageCheckerRunningState.PREVIEW_CSV ? (
                                <RunCsvChecksFloatingButtonComponent
                                    onRunCoverageChecks={
                                        performCalculationWithQuotaConfirmation
                                    }
                                    totalChecksCount={coverageChecks.length}
                                    validChecksCount={validInputRowsCount}
                                />
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

interface ICoverageCheckerCSVComponentProps {
    className?: string
}
